<template>
  <base-dialog @cancel="cancelDialog">
    <div slot="header">{{ isEdit ? $t('table.action.modify') : $t('common_258') }}</div>
    <div slot="body">
      <a-form-model ref="form" :model="form" :rules="rules" v-bind="formItemLayout" class="mcp-config-form">
        <a-form-model-item :label="$t('table.title.name')" prop="generate_name">
          <a-input v-model="form.generate_name" :placeholder="$t('common.placeholder')" />
          <template #extra>
            <name-repeated
              res="mcp_agents"
              version="v1"
              :name="form.generate_name" />
          </template>
        </a-form-model-item>
        <a-form-model-item :label="$t('aice.aiproxy.routing')" prop="aiproxy_routing_id" required>
          <base-select
            v-model="form.aiproxy_routing_id"
            resource="ai_routings"
            version="v2"
            filterable
            :params="routingSelectParams"
            :extra-opts="routingExtraOpts"
            :select-props="{ placeholder: $t('ai.mcp.select_aiproxy_routing_placeholder') }"
            style="width: 100%"
            @update:item="onRoutingItemSelected" />
          <div slot="extra">
            {{ $t('ai.mcp.aiproxy_routing_hint') }}
            <help-link href="/ai-routing">{{ $t('cloudenv.go_create') }}</help-link>
          </div>
        </a-form-model-item>
        <a-form-model-item :label="$t('aice.aiproxy.virtual_key')" prop="virtual_key_id" required>
          <base-select
            v-model="form.virtual_key_id"
            resource="ai_virtual_keys"
            version="v2"
            filterable
            :params="virtualKeySelectParams"
            :extra-opts="virtualKeyExtraOpts"
            :disabled="!selectedRoutingProjectId"
            :select-props="{ placeholder: $t('ai.mcp.select_aiproxy_virtual_key_placeholder') }"
            style="width: 100%"
            @change="onVirtualKeyChange" />
          <div slot="extra">
            {{ $t('ai.mcp.aiproxy_virtual_key_hint') }}
            <help-link href="/ai-virtual-key">{{ $t('cloudenv.go_create') }}</help-link>
          </div>
        </a-form-model-item>
        <a-form-model-item :label="$t('aice.aiproxy.client_model_select')" prop="model" required>
          <a-select
            v-model="form.model"
            :placeholder="$t('ai.mcp.select_aiproxy_model_placeholder')"
            :loading="modelOptionsLoading"
            :disabled="!form.aiproxy_routing_id"
            style="width: 100%">
            <a-select-option
              v-for="opt in clientModelOptions"
              :key="opt.id"
              :value="opt.id">
              {{ opt.id }}
              <span v-if="opt.kind === 'flat'" class="text-color-help ml-1">
                ({{ $t('aice.aiproxy.client_model_flat') }})
              </span>
            </a-select-option>
          </a-select>
        </a-form-model-item>
      </a-form-model>
    </div>
    <div slot="footer">
      <a-button type="primary" @click="handleConfirm" :loading="loading">{{ $t('dialog.ok') }}</a-button>
      <a-button @click="cancelDialog">{{ $t('dialog.cancel') }}</a-button>
    </div>
  </base-dialog>
</template>

<script>
import { Manager } from '@/utils/manager'
import DialogMixin from '@/mixins/dialog'
import WindowsMixin from '@/mixins/windows'
import { validateModelForm } from '@/utils/validate'
import NameRepeated from '@/sections/NameRepeated'
import { getAiproxyResourceProjectId, getAiproxyResourceScope, getAiproxyVirtualKeySelectParams } from '@Ai/constants/aiproxyResources'
import AiproxyVirtualKeySelectMixin from '@Ai/mixins/aiproxyVirtualKeySelectMixin'
import {
  defaultClientModelOption,
  fetchRoutingClientModelOptions,
} from '@Ai/utils/aiproxyClientModelId'
import { resolveAiproxyOpenAIBaseUrl } from '@Ai/utils/aiproxyEndpoint'

export default {
  name: 'McpConfigDialog',
  components: {
    NameRepeated,
  },
  mixins: [DialogMixin, WindowsMixin, AiproxyVirtualKeySelectMixin],
  data () {
    const { initialConfig = {} } = this.params || {}
    const isEdit = this.params.type === 'edit'
    const source = isEdit && this.params.data && this.params.data[0]
      ? this.params.data[0]
      : initialConfig
    return {
      loading: false,
      isEdit,
      modelOptionsLoading: false,
      clientModelOptions: [],
      routingExtraOpts: [],
      selectedRouting: null,
      selectedVirtualKey: null,
      form: {
        generate_name: source.name || source.generate_name || undefined,
        aiproxy_routing_id: source.aiproxy_routing_id || undefined,
        virtual_key_id: source.aiproxy_virtual_key_id || undefined,
        model: source.model || undefined,
      },
      formItemLayout: {
        wrapperCol: {
          span: 20,
        },
        labelCol: {
          span: 4,
        },
      },
      rules: {
        generate_name: [
          { required: true, message: this.$t('common.tips.input', [this.$t('table.title.name')]), trigger: ['blur', 'change'] },
        ],
        aiproxy_routing_id: [
          { required: true, message: this.$t('common.tips.select', [this.$t('aice.aiproxy.routing')]), trigger: ['change', 'blur'] },
        ],
        virtual_key_id: [
          { required: true, message: this.$t('common.tips.select', [this.$t('aice.aiproxy.virtual_key')]), trigger: ['change', 'blur'] },
        ],
        model: [
          { required: true, message: this.$t('common.tips.select', [this.$t('aice.aiproxy.client_model_select')]), trigger: ['change', 'blur'] },
        ],
      },
    }
  },
  computed: {
    data () {
      if (this.isEdit && this.params.data && this.params.data[0]) {
        return this.params.data[0]
      }
      return {}
    },
    routingSelectParams () {
      return {
        scope: getAiproxyResourceScope('ai_routings', this),
        enabled: true,
        limit: 20,
      }
    },
    selectedRoutingProjectId () {
      return getAiproxyResourceProjectId(this.selectedRouting)
    },
    virtualKeySelectParams () {
      return getAiproxyVirtualKeySelectParams(this, { enabled: true, limit: 100 }, this.selectedRouting)
    },
  },
  watch: {
    'form.aiproxy_routing_id' (val, oldVal) {
      if (val === oldVal) return
      const isInitialEditFill = this.isEdit && !oldVal
      if (!val) {
        this.selectedRouting = null
        this.routingExtraOpts = []
        this.clearVirtualKeySelection()
        this.form.model = undefined
        this.clientModelOptions = []
        return
      }
      this.ensureRoutingExtraOpt(val)
      if (isInitialEditFill) return
      this.clearVirtualKeySelection()
      this.form.model = undefined
      this.clientModelOptions = []
      this.loadClientModels(val)
    },
  },
  created () {
    if (this.form.aiproxy_routing_id) {
      this.ensureRoutingExtraOpt(this.form.aiproxy_routing_id)
      this.loadClientModels(this.form.aiproxy_routing_id, { keepCurrent: true })
    }
    if (this.form.virtual_key_id) {
      this.ensureVirtualKeyExtraOpt(this.form.virtual_key_id)
    }
  },
  methods: {
    clearVirtualKeySelection () {
      this.form.virtual_key_id = undefined
      this.virtualKeyExtraOpts = []
      this.selectedVirtualKey = null
    },
    onRoutingItemSelected (item) {
      const projectId = getAiproxyResourceProjectId(item)
      if (projectId) {
        this.selectedRouting = item
        return
      }
      if (item?.id && this.selectedRouting?.id === item.id && this.selectedRoutingProjectId) {
        return
      }
      this.selectedRouting = item || null
      if (item?.id) {
        this.ensureRoutingExtraOpt(item.id)
      }
    },
    async ensureVirtualKeyExtraOpt (id) {
      if (!id) return
      try {
        const manager = new Manager('ai_virtual_keys')
        const { data } = await manager.get({
          id,
          params: getAiproxyVirtualKeySelectParams(this, {}, this.selectedRouting),
        })
        if (data?.id) {
          this.virtualKeyExtraOpts = [{ id: data.id, name: data.name || data.id }]
          this.selectedVirtualKey = data
        }
      } catch (e) {
        this.virtualKeyExtraOpts = []
        this.selectedVirtualKey = null
      }
    },
    async onVirtualKeyChange (id) {
      if (!id) {
        this.virtualKeyExtraOpts = []
        this.selectedVirtualKey = null
        return
      }
      await this.ensureVirtualKeyExtraOpt(id)
    },
    async ensureRoutingExtraOpt (routingId) {
      if (!routingId) return
      try {
        const manager = new Manager('ai_routings')
        const { data } = await manager.get({
          id: routingId,
          params: { scope: getAiproxyResourceScope('ai_routings', this) },
        })
        if (data?.id) {
          this.routingExtraOpts = [{ id: data.id, name: data.name || data.id }]
          this.selectedRouting = data
        }
      } catch (e) {
        this.routingExtraOpts = []
      }
    },
    async loadClientModels (routingId, { keepCurrent = false } = {}) {
      const id = String(routingId || '').trim()
      if (!id) {
        this.clientModelOptions = []
        return
      }
      this.modelOptionsLoading = true
      try {
        const { options } = await fetchRoutingClientModelOptions(id, {}, this)
        this.clientModelOptions = options || []
        const current = keepCurrent ? this.form.model : ''
        if (current && this.clientModelOptions.some(opt => opt.id === current)) {
          this.form.model = current
        } else {
          this.form.model = defaultClientModelOption(this.clientModelOptions) || undefined
        }
      } catch (e) {
        this.clientModelOptions = []
        this.form.model = undefined
      } finally {
        this.modelOptionsLoading = false
      }
    },
    async buildConfig () {
      const routingId = String(this.form.aiproxy_routing_id || '').trim()
      const llmUrl = await resolveAiproxyOpenAIBaseUrl(this, { routingId })
      if (!llmUrl) {
        this.$message.error(this.$t('ai.mcp.aiproxy_endpoint_resolve_failed'))
        throw new Error(this.$t('ai.mcp.aiproxy_endpoint_resolve_failed'))
      }
      const config = {
        llm_driver: 'openai',
        llm_url: llmUrl,
        model: this.form.model,
        aiproxy_routing_id: routingId,
        aiproxy_virtual_key_id: String(this.form.virtual_key_id || '').trim(),
      }
      return config
    },
    async handleConfirm () {
      this.loading = true
      try {
        await validateModelForm(this.$refs.form)
        if (!this.selectedVirtualKey && this.form.virtual_key_id) {
          await this.ensureVirtualKeyExtraOpt(this.form.virtual_key_id)
        }
        const config = await this.buildConfig()
        if (typeof this.params.onManager === 'function') {
          if (this.isEdit) {
            config.name = this.form.generate_name
            await this.params.onManager('update', {
              id: this.params.data[0].id,
              managerArgs: {
                data: config,
              },
            })
          } else {
            config.generate_name = this.form.generate_name
            await this.params.onManager('create', {
              managerArgs: {
                data: config,
              },
            })
          }
        } else if (typeof this.params.onOk === 'function') {
          config.generate_name = this.form.generate_name
          this.params.onOk(config)
        }
        this.cancelDialog()
      } catch (error) {
        throw error
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="less" scoped>
.mcp-config-form {
  padding: 16px 0;

  ::v-deep .ant-form-item {
    margin-bottom: 24px;
  }

  ::v-deep .ant-form-item-label {
    label {
      font-weight: 500;
    }
  }
}
</style>
